import { useEffect, useState } from "react";
import { Plane, PlaneTakeoff, PlaneLanding, Radio, Clock, ExternalLink, Loader2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 60_000;

/* <LiveTrafficStrip iata="DEL" /> — fetches live aircraft positions from
   OpenSky Network via our backend. Auto-refreshes every 60s. Shows a
   graceful fallback when the feed is rate-limited or unavailable. */
export default function LiveTrafficStrip({ iata, name = "" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchFeed = async () => {
    try {
      const r = await axios.get(`${API}/api/airports/${iata}/live-traffic`);
      setData(r.data);
      setErr(false);
    } catch (e) {
      setErr(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeed();
    const t = setInterval(fetchFeed, REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iata]);

  if (loading && !data) {
    return <StripShell><Loader2 className="w-4 h-4 animate-spin" /> Fetching live airspace…</StripShell>;
  }
  if (err || !data) {
    return (
      <StripShell tone="muted">
        <Radio className="w-4 h-4" />
        Live traffic unavailable right now — try again in a minute, or open{" "}
        <a href={`https://www.flightradar24.com/airport/${iata.toLowerCase()}`} target="_blank" rel="noopener noreferrer" className="font-800 underline underline-offset-2">FlightRadar24</a>.
      </StripShell>
    );
  }

  const sampleStr = data.sample
    .slice(0, 3)
    .map((a) => a.callsign || a.icao24)
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      data-testid="live-traffic-strip"
      className="rounded-3xl border border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex p-2 rounded-xl bg-sky-500 text-white relative">
          <Radio className="w-5 h-5" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-900 text-base sm:text-lg text-sky-900">Live airspace · {iata}</h3>
            <span className="text-[10px] font-800 uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">LIVE · ADS-B</span>
            {data.notice && <span className="text-[10px] font-700 text-amber-700">· {data.notice}</span>}
          </div>
          <div className="mt-2 grid grid-cols-3 gap-3 sm:gap-5 text-sm">
            <Metric
              icon={Plane}
              label="In sector"
              value={data.aircraft_count}
              hint={`within ${data.radius_km} km`}
              testid="live-count"
            />
            <Metric
              icon={PlaneLanding}
              label="Approaching"
              value={data.approaching}
              hint="descending to land"
              tone="emerald"
              testid="live-approaching"
            />
            <Metric
              icon={PlaneTakeoff}
              label="On the ground"
              value={data.sample.filter((a) => a.on_ground).length}
              hint="taxi + stand"
              tone="amber"
              testid="live-ground"
            />
          </div>
          {sampleStr && (
            <p className="mt-3 text-[11px] font-mono text-sky-800 truncate" data-testid="live-callsigns">
              {sampleStr}{data.sample.length > 3 ? ` · +${data.sample.length - 3}` : ""}
            </p>
          )}
          <div className="mt-2 text-[11px] text-sky-700/80 inline-flex items-center gap-1.5 flex-wrap">
            <Clock className="w-3 h-3" />
            Updated {new Date((data.fetched_at || 0) * 1000).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} ·
            <span className="font-700">Source: {data.source === "adsb.lol" ? "adsb.lol (community ADS-B)" : data.source === "opensky" ? "OpenSky Network (community ADS-B)" : "ADS-B feed"}</span>
            <a
              href={`https://www.flightradar24.com/airport/${iata.toLowerCase()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-blue-700 hover:underline font-800"
            >
              Scheduled board on FR24 <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, hint, tone = "sky", testid }) {
  const text = {
    sky: "text-sky-900",
    emerald: "text-emerald-800",
    amber: "text-amber-800",
  }[tone];
  return (
    <div data-testid={testid}>
      <div className="inline-flex items-center gap-1.5 text-[10px] font-800 uppercase tracking-wider text-zinc-500">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`font-mono font-900 text-2xl ${text} tabular-nums leading-none`}>{value}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{hint}</div>
    </div>
  );
}

function StripShell({ children, tone = "sky" }) {
  const cls = tone === "muted" ? "border-zinc-200 bg-zinc-50 text-zinc-600" : "border-sky-200 bg-sky-50 text-sky-800";
  return (
    <div className={`rounded-3xl border ${cls} p-4 text-sm inline-flex items-center gap-2 flex-wrap`}>
      {children}
    </div>
  );
}
